import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";

export default function RegisterVendor() {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    type: "bar", name: "", referente: "", email: "", password: "", phone: "", address: "", city: "",
    cap: "", piva: "", hours: "", closing_days: "", offers_pickup: true, offers_delivery: false,
    delivery_radius_km: 0, delivery_min_order: 0, delivery_cost: 0, accept_privacy: false, accept_terms: false,
  });
  const set = (k, v) => setF({ ...f, [k]: v });

  useEffect(() => { api.get("/settings/public").then(({ data }) => setCities(data.cities)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.accept_privacy || !f.accept_terms) { toast.error("Devi accettare privacy e condizioni."); return; }
    setLoading(true);
    try {
      await api.post("/auth/register/vendor", {
        ...f, delivery_radius_km: Number(f.delivery_radius_km),
        delivery_min_order: Number(f.delivery_min_order), delivery_cost: Number(f.delivery_cost),
      });
      setDone(true);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="register-success">
      <div className="w-16 h-16 rounded-full bg-successbg flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8 text-successtext" /></div>
      <h1 className="mt-6 text-2xl font-bold text-navy">Registrazione ricevuta</h1>
      <p className="mt-3 text-navy/60 leading-relaxed">Il team MorningBox controllerà i dati e ti contatterà prima dell'attivazione.</p>
      <Button onClick={() => navigate("/")} className="mt-8 rounded-full bg-navy text-white h-11 px-8">Torna alla home</Button>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-12">
      <span className="text-xs uppercase tracking-[0.2em] font-semibold text-orange">Registrazione locale</span>
      <h1 className="mt-3 text-3xl lg:text-4xl font-extrabold text-navy tracking-tight">Registra il tuo bar o pasticceria</h1>
      <p className="mt-3 text-navy/60">Il team MorningBox controllerà i dati prima dell'attivazione.</p>
      <form onSubmit={submit} className="mt-8 bg-white border border-navy/10 rounded-3xl p-8 grid sm:grid-cols-2 gap-4" data-testid="register-vendor-form">
        <div>
          <Label>Tipologia</Label>
          <Select value={f.type} onValueChange={(v) => set("type", v)}>
            <SelectTrigger className="rounded-xl mt-1 h-11" data-testid="vendor-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bar">Bar</SelectItem>
              <SelectItem value="pasticceria">Pasticceria</SelectItem>
              <SelectItem value="entrambi">Entrambi</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <F label="Nome attività" v={f.name} on={(v) => set("name", v)} tid="vendor-name" required />
        <F label="Nome referente" v={f.referente} on={(v) => set("referente", v)} tid="vendor-referente" required />
        <F label="Email" type="email" v={f.email} on={(v) => set("email", v)} tid="vendor-email" required />
        <F label="Password" type="password" v={f.password} on={(v) => set("password", v)} tid="vendor-password" required />
        <F label="Telefono" v={f.phone} on={(v) => set("phone", v)} tid="vendor-phone" required />
        <F label="Partita IVA" v={f.piva} on={(v) => set("piva", v)} tid="vendor-piva" required />
        <F label="Indirizzo" v={f.address} on={(v) => set("address", v)} tid="vendor-address" required />
        <div>
          <Label>Città</Label>
          <Select value={f.city} onValueChange={(v) => set("city", v)}>
            <SelectTrigger className="rounded-xl mt-1 h-11" data-testid="vendor-city"><SelectValue placeholder="Seleziona città" /></SelectTrigger>
            <SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <F label="CAP" v={f.cap} on={(v) => set("cap", v)} tid="vendor-cap" required />
        <F label="Orari di apertura" v={f.hours} on={(v) => set("hours", v)} tid="vendor-hours" placeholder="es. 06:30 - 20:00" />
        <F label="Giorni di chiusura" v={f.closing_days} on={(v) => set("closing_days", v)} tid="vendor-closing" placeholder="es. Lunedì" />
        <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4 bg-cream rounded-2xl p-4">
          <label className="flex items-center justify-between"><span className="text-sm text-navy/70">Offro ritiro</span><Switch checked={f.offers_pickup} onCheckedChange={(v) => set("offers_pickup", v)} data-testid="vendor-pickup" /></label>
          <label className="flex items-center justify-between"><span className="text-sm text-navy/70">Offro consegna</span><Switch checked={f.offers_delivery} onCheckedChange={(v) => set("offers_delivery", v)} data-testid="vendor-delivery" /></label>
        </div>
        {f.offers_delivery && (
          <>
            <F label="Raggio consegna (km)" type="number" v={f.delivery_radius_km} on={(v) => set("delivery_radius_km", v)} tid="vendor-radius" />
            <F label="Ordine minimo consegna (€)" type="number" v={f.delivery_min_order} on={(v) => set("delivery_min_order", v)} tid="vendor-minorder" />
            <F label="Costo consegna (€)" type="number" v={f.delivery_cost} on={(v) => set("delivery_cost", v)} tid="vendor-deliverycost" />
          </>
        )}
        <div className="sm:col-span-2 space-y-2 pt-2">
          <label className="flex items-center gap-2 text-sm text-navy/70"><Checkbox checked={f.accept_privacy} onCheckedChange={(v) => set("accept_privacy", !!v)} data-testid="vendor-privacy" /> Accetto la Privacy Policy</label>
          <label className="flex items-center gap-2 text-sm text-navy/70"><Checkbox checked={f.accept_terms} onCheckedChange={(v) => set("accept_terms", !!v)} data-testid="vendor-terms" /> Accetto le condizioni del servizio</label>
        </div>
        <Button type="submit" disabled={loading} className="sm:col-span-2 rounded-full bg-navy text-white h-12" data-testid="vendor-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia registrazione"}
        </Button>
      </form>
    </div>
  );
}

function F({ label, v, on, type = "text", tid, required, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={v} required={required} placeholder={placeholder} onChange={(e) => on(e.target.value)} className="rounded-xl mt-1 h-11" data-testid={tid} />
    </div>
  );
}
